import { useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useSettings } from './shared/hooks/use-settings';
import { useDomainTracking } from './shared/hooks/use-domain-tracking';
import { Spinner } from './shared/components/spinner';
import { DomainRestriction } from './shared/components/domain-restriction';
import { BudgetPage } from './features/budget/budget-page';
import { TermsOfUsePage } from './features/terms-of-use/terms-of-use-page';
import { DomainAdminPage } from './features/domain-admin/domain-admin-page';
import { NotFoundPage } from './not-found/not-found-page';
import { trackPageView } from './shared/utils/analytics';

export function App() {
  const location = useLocation();
  const { loaded } = useSettings();
  const { info, isEmbeddingBlocked } = useDomainTracking();
  const [currentYear] = useState(() => format(new Date(), 'yyyy'));

  // Fires on mount and on every SPA route change. The standalone container's
  // Google tag has send_page_view disabled so that this is the single source of
  // page_view events rather than double-counting the initial load.
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (info.isEmbedded) {
      console.log(`Budget Calculator embedded on: ${info.parentDomain}`);
    }
  }, [info.isEmbedded, info.parentDomain]);

  return (
    <div className="min-h-screen bg-hilevel-offwhite text-hilevel-slate flex flex-col">
      <main className={`flex-1 ${!info.isEmbedded ? 'pb-16 sm:pb-12' : ''}`}>
        {!loaded ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : isEmbeddingBlocked ? (
          <DomainRestriction />
        ) : (
          <Routes>
            <Route path="/" element={<BudgetPage />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage />} />
            <Route path="/domain-admin" element={<DomainAdminPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        )}
      </main>
      {!info.isEmbedded && (
        <footer
          className="fixed inset-x-0 bottom-0 z-50 border-t border-hilevel-border bg-white/80 backdrop-blur-sm px-3 py-2 text-center"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
        >
          <p className="text-[11px] sm:text-xs text-hilevel-gray leading-tight">
            &copy; {currentYear}, The Mezzanine Floor Group Ltd. All rights reserved.{' '}
            <Link
              to="/terms-of-use"
              className="font-medium text-hilevel-slate hover:text-hilevel-red transition-colors"
            >
              Terms of Use
            </Link>
          </p>
        </footer>
      )}
    </div>
  );
}

export default App;
